/**
 * 화면이 다시 열릴 때 이전 내용을 그대로 보여주기 위한 캐시.
 *
 * 왜 필요한가
 * ----------
 * 스와이프 뒤로가기는 navigate(-1)이라, 돌아간 화면의 컴포넌트가 새로 마운트된다.
 * 대부분의 화면은 마운트하면서 다시 조회하고 그동안 로딩 화면을 그린다.
 * 그래서 돌아올 때마다 화면이 한 번 하얘졌다가 채워진다 — 깜빡임의 정체다.
 *
 * MainMy·MainHome·Reviews는 모듈 수준 변수로 각자 캐시를 갖고 있어 이 문제가
 * 없었다. 같은 장치를 한곳에 모아 나머지 화면도 쓰게 한다.
 *
 * 쓰는 법
 * ------
 *   const [review, setReview] = useState(() => pageCache.get<Review>(key))
 *   const [isLoading, setIsLoading] = useState(!pageCache.has(key))
 *   …불러온 뒤 pageCache.set(key, data)
 *
 * 캐시가 있으면 그것을 먼저 그리고, 조회는 뒤에서 조용히 끝낸다.
 *
 * 주의
 * ----
 * 메모리에만 둔다. 앱을 다시 켜면 비고, 로그아웃 때도 지워야 한다 —
 * 안 그러면 다음 사람이 이전 사람의 화면을 잠깐 보게 된다.
 */

/** 화면 하나가 이만큼 넘게 쌓이면 오래된 것부터 버린다 */
const MAX_ENTRIES = 60

const store = new Map<string, unknown>()

export const pageCache = {
  has(key: string): boolean {
    return store.has(key)
  },

  get<T>(key: string): T | null {
    return (store.get(key) as T) ?? null
  },

  set<T>(key: string, value: T): void {
    // Map은 넣은 순서를 지킨다. 가장 먼저 넣은 것이 가장 오래된 것이다.
    if (store.size >= MAX_ENTRIES && !store.has(key)) {
      const oldest = store.keys().next().value
      if (oldest !== undefined) {
        store.delete(oldest)
      }
    }
    store.set(key, value)
  },

  /**
   * 한 칸만 비운다.
   *
   * clearPrefix 로 대신하지 말 것 — 'user-profile:1' 은 'user-profile:12' 까지
   * 지운다. 키 하나를 겨냥할 때는 이걸 쓴다.
   */
  remove(key: string): void {
    store.delete(key)
  },

  /** 그 화면 종류만 비운다 (예: 리뷰를 지웠을 때) */
  clearPrefix(prefix: string): void {
    for (const key of Array.from(store.keys())) {
      if (key.startsWith(prefix)) {
        store.delete(key)
      }
    }
  },

  clear(): void {
    store.clear()
  },
}
